import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MainNavigation.scss';
import { Link, useLocation } from 'react-router-dom';
import {
    FaProjectDiagram, FaUsers, FaExclamationTriangle,
    FaAddressCard, FaTruck, FaBoxOpen, FaFileInvoiceDollar,
    FaFileInvoice, FaChevronDown, FaCoins, FaExchangeAlt,
    FaBook, FaClipboardList, FaPercentage, FaClock,
    FaChevronLeft, FaChevronRight, FaHome, FaTasks, FaBalanceScale, FaArchive, FaLock,
    FaCalculator, FaStickyNote
} from 'react-icons/fa';
import RoleGuard from '../RoleGuard';
import HeaderAvatar from '../Header/HeaderAvatar';
import { useAuth } from '../../../context/AuthContext';
import FloatingCalculator from '../FloatingCalculator/FloatingCalculator';
import FloatingNotepad from '../FloatingNotepad/FloatingNotepad';

interface MainNavigationProps {
    title: string;
}

const MainNavigation: React.FC<MainNavigationProps> = ({ title }) => {
    const { t } = useTranslation('screens');
    const { t: tc } = useTranslation('common');
    const [collapsed, setCollapsed] = useState(false);
    const [projectsOpen, setProjectsOpen] = useState(false);
    const [financeOpen, setFinanceOpen] = useState(false);
    const [inspectionsOpen, setInspectionsOpen] = useState(false);
    const [calcOpen, setCalcOpen] = useState(false);
    const [notepadOpen, setNotepadOpen] = useState(false);
    const location = useLocation();
    const { user } = useAuth();
    const tenantDomain = user?.username?.includes('@') ? user.username.split('@')[1] : null;

    const isActive = (path: string) =>
        location.pathname === path || location.pathname.startsWith(path + '/');

    const handleGroupClick = (
        setter: React.Dispatch<React.SetStateAction<boolean>>,
        others: Array<React.Dispatch<React.SetStateAction<boolean>>>
    ) => {
        if (collapsed) {
            setCollapsed(false);
            setter(true);
            others.forEach(fn => fn(false));
        } else {
            setter(o => !o);
            others.forEach(fn => fn(false));
        }
    };

    return (
        <aside className={`main-nav${collapsed ? ' main-nav--collapsed' : ''}`}>
            {/* Header / Logo */}
            <div className="main-nav__header">
                {collapsed ? (
                    <div className="main-nav__logo-icon" title={title}>
                        {title.charAt(0)}
                    </div>
                ) : (
                    <div className="main-nav__logo">
                        <span className="main-nav__logo-title">{title}</span>
                        {tenantDomain && (
                            <span
                                className="main-nav__tenant-badge"
                                title={`Active tenant: ${tenantDomain}`}
                            >
                                {tenantDomain}
                            </span>
                        )}
                    </div>
                )}
                <button
                    className="main-nav__toggle"
                    onClick={() => setCollapsed(c => !c)}
                    aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                    title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                >
                    {collapsed ? <FaChevronRight size={13} /> : <FaChevronLeft size={13} />}
                </button>
            </div>

            {/* Navigation menu */}
            <nav className="main-nav__menu" aria-label="Main navigation">
                <Link
                    to="/"
                    className={`main-nav__link${location.pathname === '/' ? ' main-nav__link--active' : ''}`}
                    title={collapsed ? t('header.home') : undefined}
                >
                    <FaHome className="main-nav__icon" />
                    {!collapsed && <span>{t('header.home')}</span>}
                </Link>

                <div className="main-nav__separator" />

                <Link
                    to="/customers"
                    className={`main-nav__link${isActive('/customers') ? ' main-nav__link--active' : ''}`}
                    title={collapsed ? t('header.customers') : undefined}
                >
                    <FaAddressCard className="main-nav__icon" />
                    {!collapsed && <span>{t('header.customers')}</span>}
                </Link>

                <Link
                    to="/suppliers"
                    className={`main-nav__link${isActive('/suppliers') ? ' main-nav__link--active' : ''}`}
                    title={collapsed ? t('header.suppliers') : undefined}
                >
                    <FaTruck className="main-nav__icon" />
                    {!collapsed && <span>{t('header.suppliers')}</span>}
                </Link>

                <Link
                    to="/products-and-services"
                    className={`main-nav__link${isActive('/products-and-services') ? ' main-nav__link--active' : ''}`}
                    title={collapsed ? t('header.productsAndServices') : undefined}
                >
                    <FaBoxOpen className="main-nav__icon" />
                    {!collapsed && <span>{t('header.productsAndServices')}</span>}
                </Link>

                <Link
                    to="/time-writing"
                    className={`main-nav__link${isActive('/time-writing') ? ' main-nav__link--active' : ''}`}
                    title={collapsed ? t('header.timeWriting') : undefined}
                >
                    <FaClock className="main-nav__icon" />
                    {!collapsed && <span>{t('header.timeWriting')}</span>}
                </Link>

                <Link
                    to="/tasks"
                    className={`main-nav__link${isActive('/tasks') ? ' main-nav__link--active' : ''}`}
                    title={collapsed ? t('header.tasks') : undefined}
                >
                    <FaTasks className="main-nav__icon" />
                    {!collapsed && <span>{t('header.tasks')}</span>}
                </Link>

                <div className="main-nav__separator" />

                {/* Projects group */}
                <div className="main-nav__group">
                    <button
                        className={`main-nav__group-toggle${
                            isActive('/projects') || isActive('/teams') || isActive('/risks')
                                ? ' main-nav__group-toggle--active'
                                : ''
                        }`}
                        onClick={() =>
                            handleGroupClick(setProjectsOpen, [setFinanceOpen, setInspectionsOpen])
                        }
                        title={collapsed ? t('header.projects') : undefined}
                        aria-expanded={!collapsed && projectsOpen}
                    >
                        <FaProjectDiagram className="main-nav__icon" />
                        {!collapsed && (
                            <>
                                <span>{t('header.projects')}</span>
                                <FaChevronDown
                                    className={`main-nav__chevron${projectsOpen ? ' main-nav__chevron--open' : ''}`}
                                />
                            </>
                        )}
                    </button>
                    {!collapsed && projectsOpen && (
                        <div className="main-nav__submenu">
                            <Link
                                to="/projects"
                                className={`main-nav__submenu-link${isActive('/projects') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaProjectDiagram className="main-nav__icon" />
                                <span>{t('header.projects')}</span>
                            </Link>
                            <Link
                                to="/teams"
                                className={`main-nav__submenu-link${isActive('/teams') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaUsers className="main-nav__icon" />
                                <span>{t('header.teams')}</span>
                            </Link>
                            <RoleGuard roles="Admin,Manager">
                                <Link
                                    to="/risks"
                                    className={`main-nav__submenu-link${isActive('/risks') ? ' main-nav__submenu-link--active' : ''}`}
                                >
                                    <FaExclamationTriangle className="main-nav__icon" />
                                    <span>{t('header.riskCatalog')}</span>
                                </Link>
                            </RoleGuard>
                        </div>
                    )}
                </div>

                {/* Finance group */}
                <div className="main-nav__group">
                    <button
                        className={`main-nav__group-toggle${
                            isActive('/customer-invoices') ||
                            isActive('/supplier-invoices') ||
                            isActive('/fixed-assets') ||
                            isActive('/bank-transactions') ||
                            isActive('/ledger-journal')
                                ? ' main-nav__group-toggle--active'
                                : ''
                        }`}
                        onClick={() =>
                            handleGroupClick(setFinanceOpen, [setProjectsOpen, setInspectionsOpen])
                        }
                        title={collapsed ? t('header.finance') : undefined}
                        aria-expanded={!collapsed && financeOpen}
                    >
                        <FaCoins className="main-nav__icon" />
                        {!collapsed && (
                            <>
                                <span>{t('header.finance')}</span>
                                <FaChevronDown
                                    className={`main-nav__chevron${financeOpen ? ' main-nav__chevron--open' : ''}`}
                                />
                            </>
                        )}
                    </button>
                    {!collapsed && financeOpen && (
                        <div className="main-nav__submenu">
                            <Link
                                to="/customer-invoices"
                                className={`main-nav__submenu-link${isActive('/customer-invoices') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaFileInvoiceDollar className="main-nav__icon" />
                                <span>{t('header.customerInvoices')}</span>
                            </Link>
                            <Link
                                to="/supplier-invoices"
                                className={`main-nav__submenu-link${isActive('/supplier-invoices') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaFileInvoice className="main-nav__icon" />
                                <span>{t('header.supplierInvoices')}</span>
                            </Link>
                            <Link
                                to="/fixed-assets"
                                className={`main-nav__submenu-link${isActive('/fixed-assets') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaArchive className="main-nav__icon" />
                                <span>{t('header.fixedAssets')}</span>
                            </Link>
                            <Link
                                to="/bank-transactions"
                                className={`main-nav__submenu-link${isActive('/bank-transactions') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaExchangeAlt className="main-nav__icon" />
                                <span>{t('header.bankTransactions')}</span>
                            </Link>
                            <Link
                                to="/ledger-journal"
                                className={`main-nav__submenu-link${isActive('/ledger-journal') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaBook className="main-nav__icon" />
                                <span>{t('header.ledgerJournal')}</span>
                            </Link>
                        </div>
                    )}
                </div>

                {/* Inspections group */}
                <div className="main-nav__group">
                    <button
                        className={`main-nav__group-toggle${
                            isActive('/vat-return') || isActive('/annual-statement') || isActive('/year-end-closing') ? ' main-nav__group-toggle--active' : ''
                        }`}
                        onClick={() =>
                            handleGroupClick(setInspectionsOpen, [setProjectsOpen, setFinanceOpen])
                        }
                        title={collapsed ? t('header.inspections') : undefined}
                        aria-expanded={!collapsed && inspectionsOpen}
                    >
                        <FaClipboardList className="main-nav__icon" />
                        {!collapsed && (
                            <>
                                <span>{t('header.inspections')}</span>
                                <FaChevronDown
                                    className={`main-nav__chevron${inspectionsOpen ? ' main-nav__chevron--open' : ''}`}
                                />
                            </>
                        )}
                    </button>
                    {!collapsed && inspectionsOpen && (
                        <div className="main-nav__submenu">
                            <Link
                                to="/vat-return"
                                className={`main-nav__submenu-link${isActive('/vat-return') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaPercentage className="main-nav__icon" />
                                <span>{t('header.vatReturn')}</span>
                            </Link>
                            <Link
                                to="/annual-statement"
                                className={`main-nav__submenu-link${isActive('/annual-statement') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaBalanceScale className="main-nav__icon" />
                                <span>{t('header.annualStatement')}</span>
                            </Link>
                            <Link
                                to="/year-end-closing"
                                className={`main-nav__submenu-link${isActive('/year-end-closing') ? ' main-nav__submenu-link--active' : ''}`}
                            >
                                <FaLock className="main-nav__icon" />
                                <span>{t('header.yearEndClosing')}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </nav>

            {/* Tools section — sits just above the avatar, clearly separated */}
            <div className="main-nav__tools-section">
                <button
                    className={`main-nav__tools-btn${calcOpen ? ' main-nav__tools-btn--active' : ''}`}
                    onClick={() => setCalcOpen(o => !o)}
                    title={tc('calculator.title')}
                    aria-pressed={calcOpen}
                >
                    <FaCalculator className="main-nav__icon" />
                    {!collapsed && <span>{tc('calculator.title')}</span>}
                </button>
                <button
                    className={`main-nav__tools-btn${notepadOpen ? ' main-nav__tools-btn--active' : ''}`}
                    onClick={() => setNotepadOpen(o => !o)}
                    title={tc('notepad.title')}
                    aria-pressed={notepadOpen}
                >
                    <FaStickyNote className="main-nav__icon" />
                    {!collapsed && <span>{tc('notepad.title')}</span>}
                </button>
            </div>

            {/* Footer: user avatar + name */}
            <div className="main-nav__footer">
                <HeaderAvatar showLabel={!collapsed} />
            </div>

            <FloatingCalculator open={calcOpen} onClose={() => setCalcOpen(false)} />
            <FloatingNotepad open={notepadOpen} onClose={() => setNotepadOpen(false)} />
        </aside>
    );
};

export default MainNavigation;


